/**
 * FreezeService is responsible for managing the freeze timeout functionality.
 * It listens to signals to initialize or cancel the freeze timeout and handles
 * the freeze logic when the timeout occurs.
 */
class FreezeService {
    /**
     * Constructs the FreezeService instance.
     * @param {Object} BridgeRuntimeService - Service for runtime bridge operations.
     * @param {Object} $timeout - AngularJS timeout service.
     * @param {Object} $window - AngularJS window service.
     * @param {Object} $signals - Service for signal handling.
     * @param {Object} $config - Configuration service.
     * @param {Object} InactivityTimeoutService - Service for managing inactivity timeouts.
     */
    constructor(BridgeRuntimeService, $timeout, $window, $signals, $config, InactivityTimeoutService) {
        this.BridgeRuntimeService = BridgeRuntimeService;
        this.$timeout = $timeout;
        this.$window = $window;
        this.$signals = $signals;
        this.$config = $config;
        this.InactivityTimeoutService = InactivityTimeoutService;
        this._logIdentifier = 'FreezeService';
        this._freezeTimer = 3000; // Default freeze timer set to 3000ms
        this._freezeTimeout = null;

        // Remove and re-register the signal listeners to ensure they are up-to-date
        this.$signals.remove("initFreezeTimeout");
        this.$signals.on("initFreezeTimeout", this.initFreezeTimeout.bind(this));

        this.$signals.remove("cancelFreezeTimeout");
        this.$signals.on("cancelFreezeTimeout", this.cancelFreezeTimeout.bind(this));
    }

    /**
     * Cancels the current freeze timeout if it exists.
     */
    cancelFreezeTimeout() {
        if (this._freezeTimeout) {
            this.$timeout.cancel(this._freezeTimeout);
            this._freezeTimeout = null;
        }
    }

    /**
     * Initializes the freeze timeout with a given timer value.
     * @param {number} freezeTimer - The timer value for the freeze timeout.
     * @returns {boolean} - Returns false if conditions for initialization are not met.
     */
    initFreezeTimeout(freezeTimer) {
        // SMELL: Hardcoded condition based on isCMS global variable and user agent check
        if ((typeof isCMS !== 'undefined' && isCMS === true) || this.$config.isPDFMode()) return false;
        if (navigator.userAgent.toLowerCase().indexOf("chrome") > -1) return false;

        this._freezeTimer = 3000; // Reset to default if not provided or invalid
        if (typeof freezeTimer === 'number' && freezeTimer > 0) {
            this._freezeTimer = freezeTimer;
        }

        this.cancelFreezeTimeout();

        this._freezeTimeout = this.$timeout(() => {
            this.freezeFn();
        }, this._freezeTimer, false);
    }

    /**
     * Executes the freeze functionality, adjusting scroll and managing inactivity timeout.
     */
    freezeFn() {
        this.InactivityTimeoutService.stopEventTrigger();
        this.$window.scrollTo(this.$window.scrollX, this.$window.scrollY + 1);
        this.$window.scrollTo(this.$window.scrollX, this.$window.scrollY - 1);
        this.$timeout(() => {
            this.InactivityTimeoutService.startEventTrigger();
        }, 100, false);


        this.cancelFreezeTimeout();

        this._freezeTimeout = this.$timeout(() => {
            this.freezeFn();
        }, this._freezeTimer, false);
    }
}

export default FreezeService;